/**
 * AnchorMarks - Auth Module
 * Handles authentication (login, register, logout, checkAuth)
 */

import { getAuthBridge } from "@contexts/context-bridge";
import { api } from "@services/api.ts";
import { showToast, closeModals } from "@utils/ui-helpers.ts";
import {
  handleApiError,
  hideServerStatusBanner,
} from "@utils/error-handler.ts";
import { logger } from "@utils/logger.ts";
import type { User } from "../../types/index";
import { confirmDialog } from "@features/ui/confirm-dialog.ts";

let authContextSetter:
  | ((user: User | null, csrf: string | null, isAuth: boolean) => void)
  | null = null;

export function setAuthContextSetter(
  setter: (user: User | null, csrf: string | null, isAuth: boolean) => void,
) {
  authContextSetter = setter;
}

// Auth form HTML templates
const LOGIN_FORM_HTML = `
  <form id="login-form" class="auth-form" method="post" autocomplete="on">
    <div class="form-group">
      <label for="login-email">Email</label>
      <input
        type="email"
        id="login-email"
        name="email"
        required
        placeholder="you@example.com"
        autocomplete="email"
      />
    </div>
    <div class="form-group">
      <div class="form-label-row">
        <label for="login-password">Password</label>
        <button type="button" class="forgot-password-link" id="forgot-password-btn">Forgot password?</button>
      </div>
      <div class="password-input-wrapper">
        <input
          type="password"
          id="login-password"
          name="password"
          required
          placeholder="••••••••"
          autocomplete="current-password"
        />
        <button type="button" class="password-toggle" aria-label="Show password" data-target="login-password">
          <svg class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          <svg class="eye-off-icon hidden" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        </button>
      </div>
    </div>
    <button type="submit" class="btn btn-primary btn-full">
      <span>Sign In</span>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    </button>
  </form>
`;

const REGISTER_FORM_HTML = `
  <form id="register-form" class="auth-form hidden" method="post" autocomplete="on">
    <div class="form-group">
      <label for="register-email">Email</label>
      <input
        type="email"
        id="register-email"
        name="email"
        required
        placeholder="you@example.com"
        autocomplete="email"
      />
    </div>
    <div class="form-group">
      <label for="register-password">Password</label>
      <div class="password-input-wrapper">
        <input
          type="password"
          id="register-password"
          name="password"
          required
          placeholder="••••••••"
          minlength="6"
          autocomplete="new-password"
        />
        <button type="button" class="password-toggle" aria-label="Show password" data-target="register-password">
          <svg class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          <svg class="eye-off-icon hidden" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        </button>
      </div>
      <div class="password-strength" id="register-password-strength">
        <div class="strength-bar">
          <div class="strength-segment"></div>
          <div class="strength-segment"></div>
          <div class="strength-segment"></div>
          <div class="strength-segment"></div>
        </div>
        <span class="strength-label"></span>
      </div>
    </div>
    <div class="form-group">
      <label for="register-confirm-password">Confirm Password</label>
      <div class="password-input-wrapper">
        <input
          type="password"
          id="register-confirm-password"
          name="confirm-password"
          required
          placeholder="••••••••"
          autocomplete="new-password"
        />
        <button type="button" class="password-toggle" aria-label="Show password" data-target="register-confirm-password">
          <svg class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          <svg class="eye-off-icon hidden" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        </button>
      </div>
      <span class="confirm-password-error hidden" id="confirm-password-error">Passwords do not match</span>
    </div>
    <button type="submit" class="btn btn-primary btn-full">
      <span>Create Account</span>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    </button>
  </form>
`;

// Show auth screen
export function showAuthScreen(): void {
  closeModals();
  const authScreen = document.getElementById("auth-screen");
  const mainApp = document.getElementById("main-app");

  if (authScreen) {
    authScreen.classList.remove("hidden");

    // Inject forms dynamically only when showing auth screen
    const formsContainer = document.getElementById("auth-forms-container");
    if (formsContainer && !document.getElementById("login-form")) {
      formsContainer.innerHTML = LOGIN_FORM_HTML + REGISTER_FORM_HTML;

      // Re-attach form listeners after injecting new forms
      import("@features/ui/forms.ts").then(({ initFormListeners }) => {
        initFormListeners();
      });
    }
  }
  if (mainApp) mainApp.classList.add("hidden");
}

// Show main app
export function showMainApp(): void {
  const authScreen = document.getElementById("auth-screen");
  const mainApp = document.getElementById("main-app");
  if (authScreen) {
    authScreen.classList.add("hidden");

    // Remove forms completely when hiding auth screen to prevent password manager detection
    const formsContainer = document.getElementById("auth-forms-container");
    if (formsContainer) {
      formsContainer.innerHTML = "";
    }
  }
  if (mainApp) mainApp.classList.remove("hidden");
}

// Prefetch CSRF Token
export async function prefetchCsrf(): Promise<void> {
  try {
    const authBridge = getAuthBridge();
    if (!authBridge.getCsrfToken()) {
      const data = await api<{ csrfToken: string }>("/auth/csrf");
      authBridge.setCsrfToken(data.csrfToken);
    }
  } catch (e) {
    logger.error("Failed to prefetch CSRF token", e);
  }
}

// Login
export async function login(email: string, password: string): Promise<boolean> {
  try {
    await prefetchCsrf();
    const data = await api<{
      csrfToken: string;
      user: User;
    }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    const authBridge = getAuthBridge();
    authBridge.setCsrfToken(data.csrfToken);
    authBridge.setCurrentUser(data.user);
    authBridge.setIsAuthenticated(true);
    authContextSetter?.(data.user, data.csrfToken, true);
    showMainApp();
    showToast("Welcome back!", "success");
    return true;
  } catch (err) {
    logger.error("Login failed", err);
    const errorMessage = handleApiError(err, true);
    showToast(errorMessage, "error");
    return false;
  }
}

// Register
export async function register(
  email: string,
  password: string,
): Promise<boolean> {
  try {
    await prefetchCsrf();
    const data = await api<{
      csrfToken: string;
      user: User;
    }>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    const authBridge = getAuthBridge();
    authBridge.setCsrfToken(data.csrfToken);
    authBridge.setCurrentUser(data.user);
    authBridge.setIsAuthenticated(true);
    authContextSetter?.(data.user, data.csrfToken, true);
    showMainApp();
    showToast("Account created successfully!", "success");
    return true;
  } catch (err) {
    logger.error("Register failed", err);
    const errorMessage = handleApiError(err, true);
    showToast(errorMessage, "error");
    return false;
  }
}

// Logout
export function logout(): void {
  api("/auth/logout", { method: "POST" })
    .catch((err) => {
      logger.error("Logout failed", err);
    })
    .finally(() => {
      const authBridge = getAuthBridge();
      authBridge.setCsrfToken(null);
      authBridge.setCurrentUser(null);
      authBridge.setIsAuthenticated(false);
      authContextSetter?.(null, null, false);
      showAuthScreen();
    });
}

// Check authentication status
export async function checkAuth(): Promise<boolean> {
  try {
    const data = await api<{
      user: User;
      csrfToken: string;
    }>("/auth/me");
    const authBridge = getAuthBridge();
    authBridge.setCurrentUser(data.user);
    authBridge.setCsrfToken(data.csrfToken);
    authBridge.setIsAuthenticated(true);
    authContextSetter?.(data.user, data.csrfToken, true);
    hideServerStatusBanner();
    return true;
  } catch (err) {
    logger.error("Auth check failed", err);
    const authBridge = getAuthBridge();
    authBridge.setCsrfToken(null);
    authBridge.setCurrentUser(null);
    authBridge.setIsAuthenticated(false);
    authContextSetter?.(null, null, false);
    showAuthScreen();
    handleApiError(err, true);
    return false;
  }
}

// Update user info display
export function updateUserInfo(): void {
  const currentUser = getAuthBridge().getCurrentUser();
  if (currentUser) {
    const userNames = document.querySelectorAll(".header-user-name");
    const userAvatars = document.querySelectorAll(".header-user-avatar");
    const userAvatarsLarge = document.querySelectorAll(
      ".header-user-avatar-large",
    );
    const apiKeyValue = document.getElementById("api-key-value");

    // Use username if available, otherwise fall back to email
    const displayName = currentUser.username || currentUser.email || "User";
    const initials = displayName.charAt(0).toUpperCase();

    userNames.forEach((el) => (el.textContent = displayName));
    userAvatars.forEach((el) => (el.textContent = initials));
    userAvatarsLarge.forEach((el) => (el.textContent = initials));
    if (apiKeyValue) apiKeyValue.textContent = currentUser.api_key || "";
  }
}

// Regenerate API key
export async function regenerateApiKey(): Promise<void> {
  if (
    !(await confirmDialog("Regenerate API key? Old keys will stop working.", {
      title: "Regenerate API Key",
      confirmText: "Regenerate",
      destructive: true,
    }))
  )
    return;

  try {
    const data = await api<{ api_key: string }>("/auth/regenerate-key", {
      method: "POST",
    });
    const currentUser = getAuthBridge().getCurrentUser();
    if (currentUser) {
      getAuthBridge().setCurrentUser({ ...currentUser, api_key: data.api_key });
    }
    const apiKeyValue = document.getElementById("api-key-value");
    if (apiKeyValue) apiKeyValue.textContent = data.api_key;
    showToast("API key regenerated!", "success");
  } catch (err) {
    logger.error("Regenerate API key failed", err);
    const errorMessage = handleApiError(err, false);
    showToast(errorMessage, "error");
  }
}

// Copy API key
export function copyApiKey(): void {
  const apiKey = getAuthBridge().getCurrentUser()?.api_key;
  if (!apiKey) return;
  navigator.clipboard.writeText(apiKey);
  showToast("API key copied!", "success");
}

// Update profile (email)
export async function updateProfile(email: string): Promise<boolean> {
  try {
    const data = await api<{ email: string }>("/auth/profile", {
      method: "PUT",
      body: JSON.stringify({ email }),
    });
    const currentUser = getAuthBridge().getCurrentUser();
    if (currentUser) {
      getAuthBridge().setCurrentUser({ ...currentUser, email: data.email });
    }
    updateUserInfo();
    showToast("Profile updated!", "success");
    return true;
  } catch (err) {
    logger.error("Update profile failed", err);
    const errorMessage = handleApiError(err, false);
    showToast(errorMessage, "error");
    return false;
  }
}

// Update password
export async function updatePassword(
  currentPassword: string,
  newPassword: string,
): Promise<boolean> {
  try {
    await api("/auth/password", {
      method: "PUT",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    showToast("Password updated successfully!", "success");
    return true;
  } catch (err) {
    logger.error("Update password failed", err);
    const errorMessage = handleApiError(err, false);
    showToast(errorMessage, "error");
    return false;
  }
}

export default {
  showAuthScreen,
  showMainApp,
  login,
  register,
  logout,
  checkAuth,
  updateUserInfo,
  regenerateApiKey,
  copyApiKey,
  updateProfile,
  updatePassword,
};
